import { useState } from "react";
import { useAuthStore } from "../store/authStore";
import { commentsAPI } from "../features/comments/commentsAPI";
import Avatar from "./Avatar";

export default function Comment({
  comment,
  reviewId,
  isReply = false,
  onEdit,
  onDelete,
  onReply,
  onRefresh,
}) {
  const { user } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(comment.content);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [repliesPage, setRepliesPage] = useState(1);
  const [hasMoreReplies, setHasMoreReplies] = useState(false);

  // Đảm bảo so sánh đúng ID
  const commentId = comment._id || comment.id;
  const userId = user?._id || user?.id;
  const commentUserId = comment.user?._id || comment.user?.id;

  const isOwner = user && commentUserId === userId;

  // Fetch replies từ API endpoint riêng
  const fetchReplies = async (page = 1, append = false) => {
    if (isReply) return; // Không fetch replies cho reply (nested level 1 only)

    setLoadingReplies(true);
    try {
      const result = await commentsAPI.getReplies(commentId, {
        page,
        limit: 10,
      });

      if (result.success) {
        const newReplies = result.data.replies || [];

        if (append) {
          setReplies((prev) => [...prev, ...newReplies]);
        } else {
          setReplies(newReplies);
        }

        setHasMoreReplies(page < result.data.totalPages);
        setRepliesPage(page);
      }
    } catch (error) {
      console.error("Error fetching replies:", error);
    } finally {
      setLoadingReplies(false);
    }
  };

  // Load more replies
  const loadMoreReplies = () => {
    if (hasMoreReplies && !loadingReplies) {
      fetchReplies(repliesPage + 1, true);
    }
  };

  // Toggle replies visibility
  const handleToggleReplies = () => {
    if (!showReplies && replies.length === 0) {
      fetchReplies(1, false);
    }
    setShowReplies(!showReplies);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editContent.trim()) return;

    setSubmitting(true);
    try {
      await onEdit(commentId, editContent);
      setIsEditing(false);
    } catch (error) {
      console.error("Error editing comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    setSubmitting(true);
    try {
      await onReply(commentId, replyContent);
      setShowReplyForm(false);
      setReplyContent("");

      // Refresh replies nếu đang hiển thị
      if (showReplies) {
        await fetchReplies(1, false);
      }

      // Refresh main comments để cập nhật replyCount
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      console.error("Error replying to comment:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("Bạn có chắc muốn xóa comment này?")) return;

    try {
      await onDelete(commentId);
    } catch (error) {
      console.error("Error deleting comment:", error);
    }
  };

  return (
    <div className={`comments__item ${isReply ? "comments__item--reply" : ""}`}>
      <div className="comments__content">
        {/* Comment Header */}
        <div className="comments__header">
          <div className="comments__user-info">
            <Avatar
              user={comment.user}
              size="sm"
              className="comments__avatar"
            />
            <div className="comments__user-details">
              <p className="comments__username">
                {comment.user?.name || "Người dùng"}
              </p>
              <p className="comments__date">{formatDate(comment.createdAt)}</p>
            </div>
          </div>

          {isOwner && (
            <div className="comments__actions">
              <button
                onClick={() => {
                  setIsEditing(true);
                  setEditContent(comment.content);
                }}
                className="comments__action-btn comments__action-btn--edit"
              >
                Sửa
              </button>
              <button
                onClick={handleDelete}
                className="comments__action-btn comments__action-btn--delete"
              >
                Xóa
              </button>
            </div>
          )}
        </div>

        {/* Comment Content */}
        {isEditing ? (
          <form onSubmit={handleEdit} className="comments__edit-form">
            <textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              className="comments__textarea"
              rows={3}
              placeholder="Chỉnh sửa comment..."
            />
            <div className="comments__form-actions">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditContent(comment.content);
                }}
                className="btn btn--ghost btn--sm"
              >
                Hủy
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="btn btn--primary btn--sm"
              >
                {submitting ? "Đang lưu..." : "Lưu"}
              </button>
            </div>
          </form>
        ) : (
          <p className="comments__text">{comment.content}</p>
        )}

        {/* Reply Actions - chỉ hiển thị cho top-level comments */}
        {!isReply && user && !isEditing && (
          <div className="comments__reply-actions">
            <button
              onClick={() => setShowReplyForm(!showReplyForm)}
              className="comments__reply-btn"
            >
              {showReplyForm ? "Hủy trả lời" : "Trả lời"}
            </button>

            {/* Toggle replies button với replyCount từ backend */}
            {comment.replyCount > 0 && (
              <button
                onClick={handleToggleReplies}
                className="comments__toggle-replies"
                disabled={loadingReplies}
              >
                {loadingReplies
                  ? "Đang tải..."
                  : showReplies
                  ? `Ẩn trả lời (${comment.replyCount})`
                  : `Xem trả lời (${comment.replyCount})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reply Form */}
      {showReplyForm && (
        <form onSubmit={handleReply} className="comments__reply-form">
          <textarea
            value={replyContent}
            onChange={(e) => setReplyContent(e.target.value)}
            className="comments__textarea"
            rows={3}
            placeholder="Viết trả lời..."
          />
          <div className="comments__form-actions">
            <button
              type="button"
              onClick={() => {
                setShowReplyForm(false);
                setReplyContent("");
              }}
              className="btn btn--ghost btn--sm"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={submitting || !replyContent.trim()}
              className="btn btn--primary btn--sm"
            >
              {submitting ? "Đang gửi..." : "Gửi"}
            </button>
          </div>
        </form>
      )}

      {/* Replies */}
      {showReplies && (
        <div className="comments__replies">
          {replies.map((reply) => {
            const replyId = reply._id || reply.id;
            return (
              <Comment
                key={replyId}
                comment={reply}
                reviewId={reviewId}
                isReply={true}
                onEdit={onEdit}
                onDelete={onDelete}
                onReply={onReply}
                onRefresh={onRefresh}
              />
            );
          })}

          {/* Load more replies button */}
          {hasMoreReplies && (
            <button
              onClick={loadMoreReplies}
              disabled={loadingReplies}
              className="comments__load-more"
            >
              {loadingReplies ? "Đang tải..." : "Xem thêm trả lời"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
